import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPhone,
  FaTimes,
  FaNetworkWired,
  FaInfoCircle,
  FaChartLine,
  FaHistory,
  FaMapMarkerAlt,
  FaClock,
} from 'react-icons/fa';
import { VoIPDevice } from '../utils/voipTypes';

interface VoIPDetailModalProps {
  isOpen: boolean;
  device: VoIPDevice | null;
  onClose: () => void;
}

const VoIPDetailModal: React.FC<VoIPDetailModalProps> = ({ isOpen, device, onClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'network' | 'asset'>('overview');
  const navigate = useNavigate();

  if (!isOpen || !device) return null;

  const handleViewHistory = () => {
    onClose();
    navigate(`/voip-device-history/${device.deviceId}`);
  };

  return (
    <div className={`modal-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className="modal voip-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <FaPhone />
            {device.name}
          </div>
          <button className="close-modal" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {/* Status Banner */}
          <div className={`device-status-banner ${device.status}`}>
            <div className="status-main">
              <span className={`status-dot ${device.status}`} />
              <span className="status-text">{device.status.toUpperCase()}</span>
              {device.vendor && device.vendor !== 'UNKNOWN' && (
                <span className={`vendor-tag ${device.vendor.toLowerCase()}`}>{device.vendor}</span>
              )}
            </div>
            <div className="status-details">
              <span className="detail-item">
                <FaMapMarkerAlt /> {device.station}
              </span>
              <span className="detail-item">
                {device.ipAddress}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="asset-tabs">
            <button
              className={`asset-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <FaInfoCircle /> Overview
            </button>
            <button
              className={`asset-tab ${activeTab === 'network' ? 'active' : ''}`}
              onClick={() => setActiveTab('network')}
            >
              <FaNetworkWired /> Network
            </button>
            <button
              className={`asset-tab ${activeTab === 'asset' ? 'active' : ''}`}
              onClick={() => setActiveTab('asset')}
            >
              <FaChartLine /> Asset Info
            </button>
          </div>

          {/* Overview Tab */}
          <div className={`asset-tab-content ${activeTab === 'overview' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaPhone /> Device Information</h4>
                <div className="info-item">
                  <span className="info-label">Device Name</span>
                  <span className="info-value">{device.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Model</span>
                  <span className="info-value">{device.model}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Firmware</span>
                  <span className="info-value">{device.firmware}</span>
                </div>
              </div>

              <div className="info-section">
                <h4><FaClock /> Status & Performance</h4>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className={`info-value ${device.status === 'online' ? 'success' : 'danger'}`}>
                    {device.status}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Registration</span>
                  <span className={`info-value ${device.registrationStatus === 'registered' ? 'success' : 'danger'}`}>
                    {device.registrationStatus}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Network Tab */}
          <div className={`asset-tab-content ${activeTab === 'network' ? 'active' : ''}`}>
            <div className="info-grid">
              <div className="info-section">
                <h4><FaNetworkWired /> Network Details</h4>
                <div className="info-item">
                  <span className="info-label">IP Address</span>
                  <span className="info-value mono">{device.ipAddress}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">MAC Address</span>
                  <span className="info-value mono">{device.macAddress}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Asset Tab */}
          <div className={`asset-tab-content ${activeTab === 'asset' ? 'active' : ''}`}>
            {device.assetDetails ? (
              <div className="info-grid">
                <div className="info-section">
                  <h4><FaInfoCircle /> Asset Details</h4>
                  <div className="info-item">
                    <span className="info-label">Vendor</span>
                    <span className={`info-value ${device.vendor && device.vendor !== 'UNKNOWN' ? 'vendor-highlight' : ''}`}>
                      {device.vendor || '-'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Make</span>
                    <span className="info-value">{device.assetDetails.make}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Model</span>
                    <span className="info-value">{device.assetDetails.model}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Serial Number</span>
                    <span className="info-value mono">{device.assetDetails.serialNumber}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Installation Date</span>
                    <span className="info-value">{device.assetDetails.installationDate}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Warranty Expiry</span>
                    <span className="info-value">{device.assetDetails.warrantyExpiry}</span>
                  </div>
                </div>

                <div className="info-section">
                  <h4><FaMapMarkerAlt /> Location Details</h4>
                  <div className="info-item">
                    <span className="info-label">Station</span>
                    <span className="info-value">{device.station}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Zone</span>
                    <span className="info-value">{device.assetDetails.zone}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Division</span>
                    <span className="info-value">{device.assetDetails.division}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Location</span>
                    <span className="info-value">{device.assetDetails.location}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-data">
                <FaInfoCircle className="no-data-icon" />
                <p>No asset details available</p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
          <button className="modal-btn primary" onClick={handleViewHistory}>
            <FaHistory /> View History
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoIPDetailModal;
